import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { LinkedApplicationsField } from './components/LinkedApplicationsField'
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { ApplicationAccessBlock, ProjectAccessTicket } from '../Requests/types'

const applicationBlockSchema = z.object({
  applicationId: z.number(),
  assignments: z
    .array(
      z.object({
        userId: z.number().optional(),
        nominee: z
          .object({
            givenName: z.string().min(1),
            familyName: z.string().min(1),
            email: z.string().email(),
          })
          .optional(),
        roleIds: z.array(z.number()).min(1),
      })
    )
    .min(1),
})

const formSchema = z.object({
  projectId: z.number().min(1, 'Project is required'),
  applications: z.array(applicationBlockSchema).min(1, 'At least one linked application is required'),
})

type FormValues = z.infer<typeof formSchema>

type RequestAccessFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTitle?: string
  organisationId?: number
}

export function RequestAccessForm(props: RequestAccessFormProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const onSubmit = useCallback(() => {
    props.onSubmit?.()
    setSheetOpen(false)
  }, [props])

  return props.sheetTrigger ? (
    <SheetWithFormComponent
      title={props.sheetTitle ?? 'Request project access'}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
      trigger={props.sheetTrigger}
    >
      <FormComponent {...props} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} onSubmit={onSubmit} />
  )
}

function FormComponent({ onSubmit, organisationId }: RequestAccessFormProps & { onSubmit: () => void }) {
  const { id: orgId } = useCurrentOrganisation(organisationId)
  const {
    getProjectsApiListOrganisationProjects,
    getSubscriptionsApiListOrganisationUsers,
    getApplicationsApiGetApplications,
    putRequestsApiAddRequest,
    getRequestsApiGetOrganisationRequests,
  } = useRestClient()

  const { data: projects } = getProjectsApiListOrganisationProjects.useQuery({ query: { orgId } })
  const { data: users } = getSubscriptionsApiListOrganisationUsers.useQuery({ query: { orgId } })
  const { data: applications } = getApplicationsApiGetApplications.useQuery(undefined)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { projectId: 0, applications: [] },
  })

  const projectOptions = useMemo(
    () => projects?.map((p) => ({ value: p.projectId!, label: p.name ?? `Project ${p.projectId}` })) ?? [],
    [projects]
  )

  const userOptions = useMemo(
    () =>
      users?.map((u) => ({
        value: u.id!,
        label: `${u.givenName} ${u.familyName}`,
        description: u.email,
      })) ?? [],
    [users]
  )

  const applicationOptions = useMemo(
    () =>
      applications?.map((a) => ({
        value: a.applicationId!,
        label: a.appName ?? `App ${a.applicationId}`,
      })) ?? [],
    [applications]
  )

  const { mutate, isPending } = putRequestsApiAddRequest.useMutation(undefined, {
    onSuccess: () => {
      getRequestsApiGetOrganisationRequests.invalidateQueries({ parameters: { query: { orgId } } })
      toast.success('Access request submitted')
      onSubmit()
    },
    onError: () => toast.error('Failed to submit access request'),
  })

  const handleSubmit = form.handleSubmit((values) => {
    const ticket: ProjectAccessTicket = {
      projectId: values.projectId,
      applications: values.applications as ApplicationAccessBlock[],
    }
    mutate({
      body: {
        ticketType: 1,
        organisationId: orgId,
        ticket: JSON.stringify(ticket),
      },
    })
  })

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <SingleSelectComboboxField
          control={form.control}
          name="projectId"
          label="Project"
          options={projectOptions}
          placeholder="Select project..."
        />
        <LinkedApplicationsField
          applicationOptions={applicationOptions}
          userOptions={userOptions}
          value={form.watch('applications')}
          onChange={(applications) => form.setValue('applications', applications, { shouldValidate: true })}
        />
        <Button type="submit" disabled={isPending}>
          Submit request
        </Button>
      </form>
    </Form>
  )
}
